<template>
  <RootPageLayout :title="title16464()">
    <section class="settings-section">
      <div class="container">
        <template v-if="settingsView.isLoading">
          <loading-spinner />
        </template>
        <template v-if="settingsView.isError">
          <LoadingError />
        </template>
        <div v-if="settingsView.isLoaded" class="settings">
          <ul class="sidebar">
            <li class="sidebar__item">
              <div class="active-bar" />
              Account
              <span class="spacer" />
              <span class="iconfont iarrow active-arrow" />
            </li>
          </ul>
          <div class="content">
            <ReadingStreakWidgetFull16464
              v-if="settingsView.readingStreakData16464"
              class="content__reading-streak16464"
              :streakData="settingsView.readingStreakData16464"
            />

            <h3>Device Notifications</h3>
            <p>Control notifications on devices where you use Shortform</p>
            <ul class="content__buttons">
              <li class="content__buttons__row">
                <ToggleButton
                  class="toggle-button"
                  :value="
                    settingsView.notificationSettings?.notify_new_release ||
                    false
                  "
                  @update:value="updateNotifyNewRelease"
                >
                  New summary announcements
                </ToggleButton>
              </li>
              <li
                v-if="streaks16464Variant() === 'B'"
                class="content__buttons__row"
              >
                <ToggleButton
                  class="toggle-button"
                  :value="
                    settingsView.notificationSettings?.notify_reading_streaks ||
                    false
                  "
                  @update:value="updateNotifyStreaks16464"
                >
                  Notifications about streaks
                </ToggleButton>
              </li>
            </ul>

            <div>
              <h3 class="settings-email">Account Email</h3>
              <p class="email-address__field">{{ email }}</p>
              <AppButton
                class="update-email__btn btns btns--yellow"
                @click="showChangeEmailPopup"
              >
                Change email
              </AppButton>
              <AppButton
                class="update-password__btn btns btns--yellow"
                @click="showChangePasswordPopup"
              >
                Change password
              </AppButton>
              <ChangePasswordPopup ref="popupChangePassword" />
              <ChangeEmailPopup ref="popupChangeEmail" @hide="recheckEmail" />
              <p
                v-if="popupChangePassword?.isSuccess()"
                class="update-password__label"
              >
                Password changed.
              </p>
              <p
                v-if="popupChangeEmail?.isSuccess()"
                class="update-email__label"
              >
                Email address changed.
              </p>
            </div>

            <InfoDeleteAccount />
          </div>
        </div>
      </div>
    </section>
  </RootPageLayout>
</template>

<script setup lang="ts">
import { onBeforeMount, ref } from 'vue'

import RootPageLayout from '@/app/components/RootPageLayout.vue'
import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'
import AppButton from '@/app/components/button/AppButton.vue'
import ToggleButton from '@/components/ui/ToggleButton.vue'
import InfoDeleteAccount from '@/app/components/settings/InfoDeleteAccount.vue'
import ChangePasswordPopup from '@/app/components/settings/ChangePasswordPopup.vue'
import ChangeEmailPopup from '@/app/components/settings/ChangeEmailPopup.vue'
import ReadingStreakWidgetFull16464 from '@/app/components/streaks/ReadingStreakWidgetFull16464.vue'

import { SettingsView } from '@/models/settings'
import { getEmail } from '@/services/auth'
import { streaks16464Variant } from '@/services/ab'

const settingsView = ref(new SettingsView())
const email = ref<string | null>(null)

function title16464(): string {
  return streaks16464Variant() === 'B' ? 'Profile' : 'Settings'
}

const popupChangePassword = ref<InstanceType<
  typeof ChangePasswordPopup
> | null>(null)

const popupChangeEmail = ref<InstanceType<typeof ChangeEmailPopup> | null>(null)

async function updateNotifyNewRelease(value: boolean): Promise<void> {
  await settingsView.value.toggleNotificationSetting(
    'notify_new_release',
    value,
  )
}

async function updateNotifyStreaks16464(value: boolean): Promise<void> {
  await settingsView.value.toggleNotificationSetting(
    'notify_reading_streaks',
    value,
  )
}

function showChangePasswordPopup(): void {
  if (!popupChangePassword.value) {
    return
  }

  popupChangePassword.value.show()
}

function showChangeEmailPopup(): void {
  if (!popupChangeEmail.value) {
    return
  }

  popupChangeEmail.value.show()
}

function recheckEmail(): void {
  // The popupChangeEmail dialog has closed.
  // Re-check the email address in case it was successfully changed.
  email.value = getEmail()
}

onBeforeMount(async () => {
  await settingsView.value.init()
  email.value = getEmail()
})
</script>

<style scoped>
.settings-section {
  margin-top: 40px;
}

.settings-email {
  margin-top: 24px;
}

.settings {
  display: flex;
  flex-flow: row nowrap;
}

.sidebar {
  display: none;
}

@media screen and (min-width: 900px) {
  .sidebar {
    display: block;
    padding: 0px;
    flex-grow: 1;
  }
}

.sidebar__item {
  max-width: 200px;

  display: flex;
  flex-flow: row-nowrap;

  line-height: 40px;
}

.active-bar {
  box-sizing: border-box;
  height: 40px;
  width: 4px;

  margin-right: 12px;

  background-color: #ffc906;
}

.spacer {
  flex-grow: 1;
}

.active-arrow {
  padding-top: 2px;
}

.content {
  flex-grow: 2;
}

.content__buttons {
  display: grid;
  gap: 24px;
  padding: 0px;
}

.content__reading-streak16464 {
  margin-bottom: 24px;
}

.update-password__btn,
.update-email__btn {
  margin-right: 12px;
  margin-bottom: 12px;
}

.update-password__label,
.update-email__label {
  margin: 12px 0;
}
</style>
